import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

/* ── Data ────────────────────────────────────────────────────────── */

interface ModuleCard {
  icon: string;
  title: string;
  description: string;
}

const MODULE_ROWS: ModuleCard[][] = [
  [
    { icon: "👥", title: "Users", description: "Manage tenant users" },
    { icon: "📊", title: "Data Sources", description: "Configure knowledge sources" },
    { icon: "📈", title: "Source Analytics", description: "Entities, sentiment & contacts" },
    { icon: "📋", title: "Jobs", description: "View ingestion jobs" },
  ],
  [
    { icon: "📉", title: "Usage", description: "View analytics and usage" },
    { icon: "📝", title: "Audit Logs", description: "View activity logs" },
    { icon: "💚", title: "System Health", description: "Monitor system status" },
    { icon: "🏢", title: "Tenants", description: "Manage tenants & invites" },
  ],
  [
    { icon: "⚙️", title: "Pipeline", description: "RAG quality & self-correction" },
    { icon: "📦", title: "Chunk Analytics", description: "Chunk types, sizes & sources" },
    { icon: "💾", title: "Database Storage", description: "Storage usage & breakdown" },
    { icon: "🔗", title: "Query Aliases", description: "Acronym & abbreviation mappings" },
  ],
];

interface ActionCard {
  icon: string;
  title: string;
  description: string;
}

const QUICK_ACTIONS: ActionCard[] = [
  { icon: "❓", title: "Generate FAQ", description: "Auto-generate Q&A from your KB" },
  { icon: "✨", title: "Backfill Entities", description: "Run entity extraction on chunks" },
  { icon: "📊", title: "Retrieval Metrics", description: "View RAG quality scores" },
  { icon: "🔔", title: "Source Health", description: "Check sync status per source" },
];

/* ── Layout constants ────────────────────────────────────────────── */

const CONTAINER_W = 1500;
const CONTAINER_H = 850;
const CONTAINER_LEFT = (1920 - CONTAINER_W) / 2;
const CONTAINER_TOP = (1080 - CONTAINER_H) / 2;

const COLS = 4;
const CARD_W = 330;
const CARD_H = 90;
const CARD_GAP = (CONTAINER_W - COLS * CARD_W) / (COLS - 1);

const ROW_DELAYS = [20, 50, 80];
const ROW_STAGGER = 8;

const ACTION_CARD_W = 330;
const ACTION_CARD_H = 100;
const ACTION_GAP = 20;

/* ── Component ───────────────────────────────────────────────────── */

export const Scene09Admin: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Global fade out 700-750
  const fadeOut = interpolate(frame, [700, 750], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Header appear 0-15
  const headerProgress = spring({
    frame,
    fps,
    config: SPRING.snappy,
  });
  const headerOpacity = interpolate(headerProgress, [0, 1], [0, 1]);
  const headerY = interpolate(headerProgress, [0, 1], [12, 0]);

  // Quick Actions section at frame 300
  const qaLabelProgress = spring({
    frame: frame - 300,
    fps,
    config: SPRING.snappy,
  });
  const qaLabelOpacity = interpolate(qaLabelProgress, [0, 1], [0, 1]);

  return (
    <AbsoluteFill>
      <GridBackground />

      <AbsoluteFill style={{ opacity: fadeOut }}>
        {/* ── Centered container ── */}
        <div
          style={{
            position: "absolute",
            left: CONTAINER_LEFT,
            top: CONTAINER_TOP,
            width: CONTAINER_W,
            height: CONTAINER_H,
          }}
        >
          {/* ── Header bar (y=80 relative to container top → absolute ~80) ── */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${COLORS.border}`,
              paddingBottom: 12,
              opacity: headerOpacity,
              transform: `translateY(${headerY}px)`,
            }}
          >
            {/* Left: icon + title */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22 }}>🛡️</span>
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 24,
                  fontWeight: 700,
                  color: COLORS.textPrimary,
                }}
              >
                Admin Dashboard
              </span>
            </div>

            {/* Right: Go to Chat button + email */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div
                style={{
                  background: COLORS.accentBlue,
                  color: "#FFFFFF",
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "6px 16px",
                  borderRadius: 8,
                  whiteSpace: "nowrap",
                }}
              >
                Go to Chat
              </div>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.textMuted,
                  whiteSpace: "nowrap",
                }}
              >
                prabhat947@gmail.com
              </span>
            </div>
          </div>

          {/* ── Module Cards Grid (y=60 below header) ── */}
          {MODULE_ROWS.map((row, rowIdx) =>
            row.map((card, colIdx) => {
              const delay = ROW_DELAYS[rowIdx] + colIdx * ROW_STAGGER;
              const cardProgress = spring({
                frame: frame - delay,
                fps,
                config: SPRING.snappy,
              });
              const cardOpacity = interpolate(cardProgress, [0, 1], [0, 1]);
              const cardY = interpolate(cardProgress, [0, 1], [20, 0]);

              const x = colIdx * (CARD_W + CARD_GAP);
              const y = 60 + rowIdx * (CARD_H + 16);

              return (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  style={{
                    position: "absolute",
                    left: x,
                    top: y,
                    width: CARD_W,
                    height: CARD_H,
                    background: COLORS.bgCard,
                    border: `1px solid ${COLORS.borderLight}`,
                    borderRadius: 14,
                    padding: "16px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    opacity: cardOpacity,
                    transform: `translateY(${cardY}px)`,
                  }}
                >
                  {/* Icon circle */}
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      minWidth: 36,
                      borderRadius: "50%",
                      background: `${COLORS.accentBlue}26`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                    }}
                  >
                    {card.icon}
                  </div>

                  {/* Text */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontSize: 15,
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        lineHeight: 1.2,
                      }}
                    >
                      {card.title}
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        color: COLORS.textMuted,
                        lineHeight: 1.3,
                      }}
                    >
                      {card.description}
                    </span>
                  </div>
                </div>
              );
            }),
          )}

          {/* ── Quick Actions section (y=500 in container) ── */}
          <div
            style={{
              position: "absolute",
              top: 500,
              left: 0,
              opacity: qaLabelOpacity,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: 20,
                fontWeight: 700,
                color: COLORS.textPrimary,
                marginBottom: 16,
              }}
            >
              Quick Actions
            </div>

            <div style={{ display: "flex", gap: ACTION_GAP }}>
              {QUICK_ACTIONS.map((action, i) => {
                const actionDelay = 310 + i * 10;
                const actionProgress = spring({
                  frame: frame - actionDelay,
                  fps,
                  config: SPRING.snappy,
                });
                const actionOpacity = interpolate(actionProgress, [0, 1], [0, 1]);
                const actionY = interpolate(actionProgress, [0, 1], [18, 0]);

                return (
                  <div
                    key={action.title}
                    style={{
                      width: ACTION_CARD_W,
                      height: ACTION_CARD_H,
                      background: COLORS.bgCard,
                      border: `1px solid ${COLORS.borderLight}`,
                      borderRadius: 12,
                      padding: "14px 18px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      opacity: actionOpacity,
                      transform: `translateY(${actionY}px)`,
                    }}
                  >
                    <span style={{ fontSize: 20, color: COLORS.accentBlue }}>
                      {action.icon}
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontSize: 14,
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        lineHeight: 1.2,
                      }}
                    >
                      {action.title}
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        color: COLORS.textMuted,
                        lineHeight: 1.3,
                      }}
                    >
                      {action.description}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
